import {Component, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import {FormProfile} from '@modules/account-management/core/form-profil.class';
import {ProfileService} from '@modules/account-management/core/profile/profile.service';
import {DataEntity, OctopusConnectService} from 'octopus-connect';
import {AuthenticationService} from '@modules/authentication';
import {isTypeOf} from "shared/utils";

@Component({
    selector: 'fuse-edit-profile-forms',
    templateUrl: './forms.component.html',
    styleUrls: ['./forms.component.scss']
})
export class FuseFormsComponent implements OnInit {
    form: UntypedFormGroup;
    formErrors: any;

    public canEditAvatar: boolean;
    public learnerCanEditMail: boolean;
    public allowedExtensions: string;
    public allowedMaxSize: string;
    public isPngTooBig = false;
    public formIsReady = false;
    public displayGenericError = false;

    userInformation: DataEntity;
    defaultUserInformation: FormProfile;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private profileProvider: ProfileService,
        public authenticationService: AuthenticationService,
        private octopusConnect: OctopusConnectService
    ) {
        this.defaultUserInformation = new FormProfile({}, this.octopusConnect);
        // Reactive form errors
        this.formErrors = {
            label: {},
            email: {},
            you_are: {},
            find_us: {},
            region: {},
            level: {},
            password: {},
            passwordConfirm: {},
        };
    }

    get userIsAdult(): boolean {
        if (!this.userInformation.get('contact_email')) {
            return true;
        }
        return false;
    }

    ngOnInit(): void {
        this.defaultUserInformation.completeLoading.subscribe(() => {
            this.initialize();
        });
    }

    requiredFields(name): ValidationErrors | void {
        if (this.displayField(name) && !this.userInformation.get('sso')) {
            return Validators.required;
        }
    }

    /***
     * this.isPngTooBig is use to change color of text
     * @param isSizeTooBig : true if file is too big to be upload
     */
    public changeColorIfTooBig(isSizeTooBig: boolean): void {
        this.isPngTooBig = isSizeTooBig;
    }

    onFormValuesChanged(): void {
        for (const field in this.formErrors) {
            if (!this.formErrors.hasOwnProperty(field)) {
                continue;
            }

            // Clear previous errors
            this.formErrors[field] = {};

            // Get the control
            const control = this.form.get(field);

            if (control && control.dirty && !control.valid) {
                this.formErrors[field] = control.errors;
            }

            if (this.form.get('password').value !== '' && this.form.get('passwordConfirm').value === '') {
                this.form.get('passwordConfirm').setErrors({passwordsNotMatch: true});
                this.formErrors['passwordConfirm'] = this.form.get('passwordConfirm').errors;
            } else {
                if (this.form.get('password').value === '' && this.form.get('passwordConfirm').value === '') {
                    this.form.get('passwordConfirm').setErrors(null);
                }
            }

        }
    }

    editProfile(): void {
        this.displayGenericError = false;

        if (!this.form.invalid) {
            const user = {
                label: this.form.value.label,
                email: this.form.value.email,
                you_are: this.form.value.you_are,
                find_us: this.form.value.find_us,
                region: this.form.value.region, // id is store in .region directly
                level: this.form.value.level,
                newsletter: this.form.value.newsletter,
                role_field: this.form.value.role_field,
                password: this.form.value.password,
                institution: this.form.value.institution
            };
            if (!user.level) {
                // if we update only pseudo (label) we don't want to suppress level data
                delete user.level;
            }
            this.profileProvider.editUser(user).subscribe(isSuccess => {
                if (isSuccess === false) {
                    this.displayGenericError = true;
                } else {
                    const users = JSON.parse(localStorage.getItem('localUsers'));
                    const i = users.findIndex(localUser => localUser.codeid === this.userInformation.get('codeid'));
                    if (i !== -1) {
                        users[i].label = user.label;
                        localStorage.setItem('localUsers', JSON.stringify(users));
                        this.userInformation.set('label', user.label);
                    }
                }
            });
        }
    }

    setMode(mode: boolean): void {
        this.form = this.formBuilder.group({
            label: [this.userInformation.get('label'), Validators.required],
            email: [this.userInformation.get('email'), [Validators.required, Validators.email]],
            you_are: [this.userInformation.get('you_are'), Validators.required],
            find_us: [this.userInformation.get('find_us'), Validators.required],
            region: [this.userInformation.get('region'), Validators.required],
            level: [this.userInformation.get('level'), Validators.required],
            newsletter: [this.userInformation.get('newsletter')],
            password: [''],
            passwordConfirm: ['', confirmPassword],
            institution: ['']

        });

        this.profileProvider.editMode = mode;
    }

    displayField(name: string): boolean {
        return this.profileProvider.getFields().some(f => f.label === name);
    }

    /**
     * return the list of possible choice
     */
    public youAreValues(): string[] {
        return this.profileProvider.youAreValues();
    }

    private initialize(): void {
        this.allowedExtensions = this.profileProvider.getAllowedExtensions().join(', ');
        this.allowedMaxSize = this.profileProvider.getAllowedMaxSize().toString();
        this.canEditAvatar = this.profileProvider.getCanEditAvatar();
        this.learnerCanEditMail = this.profileProvider.learnerCanEditMail();
        this.profileProvider.userInformationOnChanged.subscribe(userInformation => {
            if (isTypeOf<DataEntity>(userInformation, 'get'))
                this.userInformation = userInformation;
        });

        // Reactive Form
        this.form = this.formBuilder.group({
            label: [this.userInformation.get('label'), Validators.required],
            email: [{
                value: this.userInformation.get('email'),
                disabled: this.userInformation.get('sso')
            }, [Validators.required, Validators.email]],
            you_are: [this.userInformation.get('you_are'), this.requiredFields('you_are')],
            find_us: [this.userInformation.get('find_us'), this.requiredFields('find_us')],
            region: [this.userInformation.get('region') ? +this.userInformation.get('region').id : '', this.requiredFields('region')],
            level: [this.userInformation.get('level') ? +this.userInformation.get('level').id : null, this.requiredFields('level')],
            newsletter: [this.userInformation.get('newsletter')],
            password: [''],
            passwordConfirm: ['', confirmPassword],
            institution: this.userInformation.get('institution')
        });

        this.form.valueChanges.subscribe(() => {
            this.onFormValuesChanged();
        });

        this.formIsReady = true;
    }

}


function confirmPassword(control: AbstractControl): { passwordsNotMatch: true } {
    if (!control.parent || !control) {
        return;
    }

    const password = control.parent.get('password');
    const passwordConfirm = control.parent.get('passwordConfirm');

    if (!password || !passwordConfirm) {
        return;
    }

    if (password.value !== '' && passwordConfirm.value === '') {
        return {
            passwordsNotMatch: true
        };
    }

    if (passwordConfirm.value === '') {

        return;

    }

    if (password.value !== passwordConfirm.value) {
        return {
            passwordsNotMatch: true
        };
    }
}
